import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { apiFetch } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

// "Conectei. E agora?" — o caminho do projeto oficial até o primeiro envio de
// verdade. Cada passo se confere sozinho com o que a API já sabe: ninguém marca
// nada à mão, e o cartão some quando tudo está feito.

interface Tpl { status: string }

function useOfficialProgress() {
  const allTime = `/usage?from=${encodeURIComponent('2020-01-01T00:00:00Z')}&to=${encodeURIComponent(new Date().toISOString())}`;
  const usage = useQuery({
    queryKey: ['official-checklist', 'usage'],
    queryFn: async () => (await apiFetch('GET', allTime)).data as { received?: number; by_type?: Record<string, number> } ?? {},
  });
  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: async () => ((await apiFetch('GET', '/templates')).data as { templates?: Tpl[] })?.templates ?? [],
  });
  const fallback = useQuery({
    queryKey: ['templates', 'fallback'],
    queryFn: async () => ((await apiFetch('GET', '/templates/fallback')).data as { template?: unknown | null })?.template ?? null,
  });
  const hooks = useQuery({
    queryKey: ['webhooks'],
    queryFn: async () => ((await apiFetch('GET', '/webhooks')).data as { data?: unknown[] })?.data ?? [],
  });
  const loading = usage.isLoading || templates.isLoading || fallback.isLoading || hooks.isLoading;
  return {
    loading,
    received: (usage.data?.received ?? 0) > 0,
    approved: (templates.data ?? []).some((t) => t.status === 'APPROVED'),
    fallback: !!fallback.data,
    webhook: (hooks.data ?? []).length > 0,
    sentTemplate: (usage.data?.by_type?.template ?? 0) > 0,
  };
}

export function OfficialChecklist() {
  const t = useT();
  const p = useOfficialProgress();
  if (p.loading) return null;
  const steps = [
    { done: p.received, key: 'received', to: '/numbers' },
    { done: p.approved, key: 'approved', to: '/templates' },
    { done: p.fallback, key: 'fallback', to: '/templates' },
    { done: p.webhook, key: 'webhook', to: '/webhooks' },
    { done: p.sentTemplate, key: 'sentTemplate', to: '/playground' },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  if (doneCount === steps.length) return null;
  const next = steps.find((s) => !s.done);

  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          {t('officialChecklist.title')}
          <span className="text-sm font-normal text-muted-foreground">
            {t('officialChecklist.progress', { done: doneCount, total: steps.length })}
          </span>
        </CardTitle>
        <p className="text-sm text-muted-foreground">{t('officialChecklist.subtitle')}</p>
      </CardHeader>
      <CardContent className="flex flex-col gap-1">
        {steps.map((s) => (
          <Link
            key={s.key}
            to={s.to}
            className={cn('flex items-start gap-3 rounded-md px-2 py-2 transition-colors hover:bg-accent/50',
              s === next && 'bg-primary/5')}
          >
            {s.done
              ? <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-500" />
              : <Circle className="mt-0.5 size-4 shrink-0 text-muted-foreground" />}
            <span className="flex-1">
              <span className={cn('block text-sm font-medium', s.done && 'text-muted-foreground line-through')}>
                {t(`officialChecklist.${s.key}.title`)}
              </span>
              {!s.done && <span className="block text-xs text-muted-foreground">{t(`officialChecklist.${s.key}.hint`)}</span>}
            </span>
            {s === next && <ArrowRight className="mt-0.5 size-4 shrink-0 text-primary" />}
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}
